'use client'

import React, { useRef } from 'react'

import { useOverlayStack } from './useOverlayStack'
import ReactDOM from 'react-dom'

import {
  Box,
  ChakraProvider,
  SystemStyleObject,
  chakra,
} from '@chakra-ui/react'

import { system } from '../../theme/system'
import { ColorScheme } from '../../types'
import { Button } from '../Button'
import { CloseIcon } from '../Icon'
import {
  bodyStyles,
  borderLayerStyles,
  catEarStyles,
  containerStyles,
  containerWrapperStyles,
  footerStyles,
  headerContentStyles,
  headerStyles,
  noteStyles,
  overlayStyles,
  titleStyles,
} from './Modal.styles'
import type { ModalSize } from './Modal.styles'

export type { ModalSize } from './Modal.styles'

export type HeaderStyleOverrides = {
  /** Styles applied to the header container box. */
  container?: SystemStyleObject
  /** Styles applied to the title element (merged after defaults). */
  title?: SystemStyleObject
  /** Styles applied to the header content (the `header` prop). */
  content?: SystemStyleObject
}

export type ModalProps = {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  onSave?: () => void
  isSaveDisabled?: boolean
  isSaveLoading?: boolean
  isSaveHidden?: boolean
  /** When true, hides the top-right close (×) button so the modal has no
   * dismiss affordance of its own (e.g. a gated onboarding step). */
  isCloseHidden?: boolean
  /** Extra content rendered in the header, below description */
  header?: React.ReactNode
  /** Extra content rendered in the footer, before the Save button */
  footer?: React.ReactNode
  /** Content rendered in a dedicated section between the body and the footer. */
  note?: React.ReactNode
  colorScheme?: ColorScheme
  /**
   * Override the default max-width of the modal (e.g. '800px' for wide
   * content like a scheduler). Defaults to '1143.5px' for 'default' size
   * and '404.15px' for 'compact'.
   */
  maxW?: string
  /**
   * Preset dimensions. Existing `default` and `compact` behavior is preserved;
   * `medium` is 640×400 and `large` is 880×500.
   */
  size?: ModalSize
  /** Style overrides for the header section (container, title, and content sub-fields). */
  headerStyleOverrides?: HeaderStyleOverrides
  /** Style overrides applied to the body wrapper element. */
  bodyStyleOverrides?: SystemStyleObject
  /** Style overrides applied to the footer wrapper element. */
  footerStyleOverrides?: SystemStyleObject
  /** Style overrides applied to the note section wrapper. */
  noteStyleOverrides?: SystemStyleObject
  /** Label for the primary action button. Defaults to 'Save'. */
  saveButtonLabel?: string
  /** When true, renders the primary action button with the danger variant. */
  isSaveDestructive?: boolean
  /** When true, renders a Cancel button in the footer before the primary action button. */
  showCancelButton?: boolean
  /** Label for the secondary action button. Defaults to 'Cancel'. */
  cancelButtonLabel?: string
  /** Handler for the Cancel button. Falls back to onClose if not provided. */
  onCancel?: () => void
  /** Override the overlay z-index. Defaults to 1000. */
  zIndex?: number
  /**
   * Set false to keep Escape from dismissing the modal. Use for a form whose
   * in-progress input would be lost; the explicit close, Cancel and Save
   * controls are unaffected.
   */
  closeOnEsc?: boolean
  /** Set false to keep a click on the overlay from dismissing the modal. */
  closeOnOverlayClick?: boolean
  /** Style overrides applied to the modal container. */
  containerStyleOverrides?: SystemStyleObject
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  isSaveDisabled = false,
  isSaveLoading = false,
  isSaveHidden = false,
  isCloseHidden = false,
  header,
  footer,
  colorScheme = 'dark',
  maxW,
  size = 'default',
  headerStyleOverrides,
  bodyStyleOverrides,
  footerStyleOverrides,
  note,
  noteStyleOverrides,
  saveButtonLabel = 'Save',
  isSaveDestructive = false,
  showCancelButton = false,
  cancelButtonLabel = 'Cancel',
  onCancel,
  zIndex,
  containerStyleOverrides,
  closeOnEsc = true,
  closeOnOverlayClick = true,
}: ModalProps) => {
  const textColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Stack membership, Escape/Tab trap, and focus save/restore - shared with
  // Drawer via useOverlayStack.
  useOverlayStack({ isOpen, onClose, wrapperRef, closeOnEsc })

  if (!isOpen || typeof document === 'undefined') return null

  return ReactDOM.createPortal(
    <ChakraProvider value={system}>
      {/* data-no-focus-lock exempts this portal from any active react-focus-lock
          trap (e.g. a Chakra v1 Drawer with trapFocus) — without it, the trap
          reclaims focus from the modal's inputs on every click. */}
      <Box
        data-no-focus-lock
        data-testid='modal-overlay'
        {...overlayStyles(colorScheme, zIndex)}
        onClick={closeOnOverlayClick ? onClose : undefined}
      >
        {/* Outer: focus trap boundary + cat ear positioning — no clip-path */}
        <Box
          display='inline-flex'
          filter='drop-shadow(0 4px 24px rgba(0, 0, 0, 0.2))'
          onClick={(e) => e.stopPropagation()}
          outline='none'
          position='relative'
          ref={wrapperRef}
          tabIndex={-1}
        >
          {/* Border layer: SIBLING of clip wrapper, slightly larger, same polygon — peeks out as the stroke */}
          <Box {...borderLayerStyles(colorScheme)} />

          {/* Inner: clip-path wrapper */}
          <Box {...containerWrapperStyles}>
            <Box
              aria-labelledby='modal-title'
              aria-modal='true'
              onClick={(e) => e.stopPropagation()}
              ref={containerRef}
              role='dialog'
              tabIndex={-1}
              {...containerStyles(colorScheme, maxW, size)}
              {...containerStyleOverrides}
            >
              {/* Header */}
              <Box {...headerStyles(size)} {...headerStyleOverrides?.container}>
                <Box
                  id='modal-title'
                  {...titleStyles(colorScheme, size)}
                  {...headerStyleOverrides?.title}
                >
                  {title}
                </Box>
                {header && (
                  <Box
                    color={textColor}
                    {...headerContentStyles}
                    {...headerStyleOverrides?.content}
                  >
                    {header}
                  </Box>
                )}
              </Box>

              {/* Body */}
              <Box
                color={textColor}
                {...bodyStyles(size, colorScheme)}
                {...bodyStyleOverrides}
              >
                {children}
              </Box>

              {/* Note */}
              {note && (
                <Box color={textColor} {...noteStyles} {...noteStyleOverrides}>
                  {note}
                </Box>
              )}

              {/* Footer */}
              {(!isSaveHidden || footer) && (
                <Box {...footerStyles(size)} {...footerStyleOverrides}>
                  {footer}
                  {!isSaveHidden && onSave && (
                    <>
                      {showCancelButton && (
                        <Button
                          colorScheme={colorScheme}
                          onClick={onCancel ?? onClose}
                          variant={
                            colorScheme === 'light' ? 'tertiary' : 'secondary'
                          }
                        >
                          {cancelButtonLabel}
                        </Button>
                      )}
                      <Button
                        colorScheme={colorScheme}
                        disabled={isSaveDisabled}
                        isLoading={isSaveLoading}
                        onClick={onSave}
                        variant={isSaveDestructive ? 'danger' : 'primary'}
                      >
                        {saveButtonLabel}
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Cat ear - outside the clip-path so it remains visible */}
          {!isCloseHidden && (
            <chakra.button
              aria-label='Close'
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              type='button'
              {...catEarStyles(colorScheme)}
            >
              <CloseIcon color='currentColor' />
            </chakra.button>
          )}
        </Box>
      </Box>
    </ChakraProvider>,
    document.body
  )
}
